import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronDown, Plus, RotateCcw, X } from "lucide-react";
import { AUDIENCE_SEGMENT_VALUES, type DrilldownFilterPresetDto } from "@nexus/contracts";
import { DrilldownFilterPresetSelector } from "@/features/dashboard/components/filter-presets/DrilldownFilterPresetSelector";
import { DrilldownSaveFilterPreset } from "@/features/dashboard/components/filter-presets/DrilldownSaveFilterPreset";
import {
  Button,
  Checkbox,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@nexus/ui";
import {
  AdvancedFiltersEditor,
  type FilterFieldOption,
} from "@/components/advanced-filters/AdvancedFiltersEditor";
import { useDashboardFiltersStore } from "@/features/dashboard/stores/dashboardFilters.store";
import {
  DEFAULT_SEGMENT_1,
  EFFECTIVE_STATUS_OPTIONS,
  MY_BRAND_EFFECTIVE_STATUS_OPTIONS,
  MY_BRAND_INACTIVE_STATUSES,
  MY_BRAND_OBJECT_TYPE_OPTIONS,
  OBJECT_TYPE_OPTIONS,
  SEGMENT_NONE,
  fallbackSegmentOptions,
  segmentLabel,
  useDrilldownFiltersStore,
  type SegmentOption,
} from "../stores/drilldownFilters.store";
import { drilldownGroupByOptionsQueryOptions } from "@/lib/queries/drilldown.queries";
import { useKpiSelectorCatalog } from "@/features/kpi-settings/hooks/useKpiSelectorCatalog";
import { audienceSegmentLabel } from "@/lib/audience-segment-labels";
import {
  FILTER_TRIGGER_BASE,
  FILTER_TRIGGER_LABEL,
  FILTER_TRIGGER_VALUE,
} from "@/features/dashboard/lib/filter-trigger-styles";

const FILTER_TRIGGER_BUTTON = cn(
  FILTER_TRIGGER_BASE,
  "inline-flex min-w-0 items-center gap-1.5",
);

function arraysEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** Order-independent set equality — dedupes both sides first so a selection with an
 *  accidental duplicate can't false-positive against a shortcut of a different size. */
function arraysEqualAsSet(a: string[], b: string[]): boolean {
  const setA = new Set(a);
  const setB = new Set(b);
  if (setA.size !== setB.size) return false;
  for (const v of setA) if (!setB.has(v)) return false;
  return true;
}

function buildFilterFieldOptions(
  mode: "competitors" | "my-brand",
  segment1: string,
  segment2: string,
  segment3: string,
  myBrandKpis: string[],
  catalogById: Record<string, { id: string; label: string }>,
): FilterFieldOption[] {
  const opts: FilterFieldOption[] = [];
  if (segment1 !== SEGMENT_NONE) {
    opts.push({ key: "Groupby1", label: segmentLabel(segment1), type: "string" });
  }
  if (segment2 !== SEGMENT_NONE) {
    opts.push({ key: "Groupby2", label: segmentLabel(segment2), type: "string" });
  }
  if (segment3 !== SEGMENT_NONE) {
    opts.push({ key: "Groupby3", label: segmentLabel(segment3), type: "string" });
  }
  if (mode === "my-brand") {
    // Only the currently-selected KPI pills — not the full static catalog — mirroring
    // how only *active* segments (not all SEGMENT_OPTIONS) show up above.
    for (const id of myBrandKpis) {
      const entry = catalogById[id];
      if (entry) opts.push({ key: entry.id, label: entry.label, type: "numeric" });
    }
  } else {
    opts.push(
      { key: "Days", label: "Days", type: "numeric" },
      { key: "Assets", label: "Assets", type: "numeric" },
      { key: "DaysPerAsset", label: "Days/Assets", type: "numeric" },
    );
  }
  return opts;
}

function MultiSelectPill({
  label,
  emptyLabel,
  options,
  selected,
  onChange,
  shortcuts,
}: {
  label: string;
  emptyLabel: string;
  options: readonly SegmentOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  shortcuts?: { label: string; values: string[] }[];
}) {
  const allExplicit = selected.length === options.length;

  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );

  const summary =
    selected.length === 0 || allExplicit
      ? emptyLabel
      : options.filter((o) => selected.includes(o.value)).map((o) => o.label).join(", ");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={FILTER_TRIGGER_BUTTON}>
          <span className={FILTER_TRIGGER_LABEL}>{label}:</span>
          <span className={cn("min-w-0 truncate", FILTER_TRIGGER_VALUE)}>{summary}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-50" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="flex flex-col gap-1">
          <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium hover:bg-muted">
            <Checkbox
              checked={allExplicit}
              onCheckedChange={() => onChange(allExplicit ? [] : options.map((o) => o.value))}
              className="h-3.5 w-3.5"
            />
            <span>All</span>
          </label>
          {shortcuts?.map((s) => {
            const isActive = arraysEqualAsSet(selected, s.values);
            return (
              <label
                key={s.label}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium hover:bg-muted"
              >
                <Checkbox
                  checked={isActive}
                  onCheckedChange={() => onChange(isActive ? [] : s.values)}
                  className="h-3.5 w-3.5"
                />
                <span>{s.label}</span>
              </label>
            );
          })}
          <div className="my-0.5 border-t border-border/50" />
          {options.map((o) => (
            <label
              key={o.value}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-muted"
            >
              <Checkbox
                checked={selected.includes(o.value)}
                onCheckedChange={() => toggle(o.value)}
                className="h-3.5 w-3.5"
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SegmentPill({
  value,
  onChange,
  onRemove,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  onRemove?: () => void;
  options: readonly SegmentOption[];
}) {
  const displayed = options.find((o) => o.value === value)?.label ?? segmentLabel(value);
  const dropdownOptions =
    options.length > 0
      ? options
      : [{ label: displayed, value }];

  return (
    <span className="inline-flex items-center">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={cn(FILTER_TRIGGER_BUTTON, onRemove && "rounded-r-none border-r-0")}>
          <span className={FILTER_TRIGGER_LABEL}>Group By:</span>
          <span className={cn("min-w-0 truncate", FILTER_TRIGGER_VALUE)}>{displayed}</span>
        </SelectTrigger>
        <SelectContent>
          {dropdownOptions.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove group by"
          className={cn(
            FILTER_TRIGGER_BASE,
            "inline-flex h-8 items-center rounded-l-none border-l-0 px-2 text-muted-foreground hover:text-foreground",
          )}
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </span>
  );
}

export function DrilldownFiltersToolbar({
  trailingActions,
  onPresetApplied,
  filtersHydrated = true,
  applySignal,
  onPendingPresetChange,
  canSavePreset = true,
}: {
  trailingActions?: ReactNode;
  onPresetApplied?: (preset: DrilldownFilterPresetDto) => void;
  filtersHydrated?: boolean;
  applySignal?: number;
  onPendingPresetChange?: (hasPending: boolean) => void;
  canSavePreset?: boolean;
}) {
  const { t } = useTranslation();
  const [selectPresetId, setSelectPresetId] = useState<string | null>(null);
  const { mode } = useDashboardFiltersStore();
  const isMyBrand = mode === "my-brand";
  const { myBrandById } = useKpiSelectorCatalog("drilldown");
  const { data: fetchedSegmentOptions, isError: isGroupByOptionsError } = useQuery(
    drilldownGroupByOptionsQueryOptions(isMyBrand ? "my-brand" : "competitors"),
  );
  const segmentOptions = isGroupByOptionsError
    ? fallbackSegmentOptions(isMyBrand ? "my-brand" : "competitors")
    : (fetchedSegmentOptions ?? []);

  const {
    objectType,
    effectiveStatus,
    myBrandObjectType,
    myBrandEffectiveStatus,
    myBrandKpis,
    startDate,
    endDate,
    myBrandStartDate,
    myBrandEndDate,
    segment1,
    segment2,
    segment3,
    myBrandSegment1,
    myBrandSegment2,
    myBrandSegment3,
    advancedFilters,
    myBrandAdvancedFilters,
    appliedObjectType,
    appliedEffectiveStatus,
    appliedMyBrandObjectType,
    appliedMyBrandEffectiveStatus,
    appliedStartDate,
    appliedEndDate,
    appliedMyBrandStartDate,
    appliedMyBrandEndDate,
    appliedSegment1,
    appliedSegment2,
    appliedSegment3,
    appliedMyBrandSegment1,
    appliedMyBrandSegment2,
    appliedMyBrandSegment3,
    appliedAdvancedFilters,
    appliedMyBrandAdvancedFilters,
    audienceSegment,
    appliedAudienceSegment,
    setObjectType,
    setEffectiveStatus,
    setMyBrandObjectType,
    setMyBrandEffectiveStatus,
    setAudienceSegment,
    setSegment,
    addSegment,
    removeSegment,
    addFilterGroup,
    removeFilterGroup,
    setGroupConnector,
    addFilterRule,
    removeFilterRule,
    updateFilterRule,
    reset,
  } = useDrilldownFiltersStore();

  // Ad Type / Status have their own value domain per mode (mirrors competitorSortMode
  // vs myBrandSortMode) — resolve the active pair once here.
  const objectTypeOptions = isMyBrand ? MY_BRAND_OBJECT_TYPE_OPTIONS : OBJECT_TYPE_OPTIONS;
  const effectiveStatusOptions = isMyBrand ? MY_BRAND_EFFECTIVE_STATUS_OPTIONS : EFFECTIVE_STATUS_OPTIONS;
  const activeObjectType = isMyBrand ? myBrandObjectType : objectType;
  const activeAppliedObjectType = isMyBrand ? appliedMyBrandObjectType : appliedObjectType;
  const setActiveObjectType = isMyBrand ? setMyBrandObjectType : setObjectType;
  const activeEffectiveStatus = isMyBrand ? myBrandEffectiveStatus : effectiveStatus;
  const activeAppliedEffectiveStatus = isMyBrand ? appliedMyBrandEffectiveStatus : appliedEffectiveStatus;
  const setActiveEffectiveStatus = isMyBrand ? setMyBrandEffectiveStatus : setEffectiveStatus;
  // Advanced filters have their own value domain per mode too (Groupby fields +
  // Days/Assets for Competitors vs Groupby fields + KPI ids for My Brand) — same
  // namespacing, so switching modes doesn't show one mode's rules against the other's
  // fields. The mutation actions (addFilterGroup etc.) resolve the active pair
  // internally; only the DISPLAYED value needs resolving here.
  const activeAdvancedFilters = isMyBrand ? myBrandAdvancedFilters : advancedFilters;
  const activeAppliedAdvancedFilters = isMyBrand ? appliedMyBrandAdvancedFilters : appliedAdvancedFilters;
  // Group By segments are namespaced per mode too (own remembered selection,
  // options fetched per mode from features_mapping) — same resolution pattern as above.
  const activeSegment1 = isMyBrand ? myBrandSegment1 : segment1;
  const activeSegment2 = isMyBrand ? myBrandSegment2 : segment2;
  const activeSegment3 = isMyBrand ? myBrandSegment3 : segment3;
  const activeAppliedSegment1 = isMyBrand ? appliedMyBrandSegment1 : appliedSegment1;
  const activeAppliedSegment2 = isMyBrand ? appliedMyBrandSegment2 : appliedSegment2;
  const activeAppliedSegment3 = isMyBrand ? appliedMyBrandSegment3 : appliedSegment3;
  // Date range is namespaced per mode too (own remembered range) — same resolution.
  const activeStartDate = isMyBrand ? myBrandStartDate : startDate;
  const activeEndDate = isMyBrand ? myBrandEndDate : endDate;
  const activeAppliedStartDate = isMyBrand ? appliedMyBrandStartDate : appliedStartDate;
  const activeAppliedEndDate = isMyBrand ? appliedMyBrandEndDate : appliedEndDate;

  const objectTypeModified = !arraysEqual(activeObjectType, activeAppliedObjectType);
  const effectiveStatusModified = !arraysEqual(activeEffectiveStatus, activeAppliedEffectiveStatus);
  const audienceSegmentModified = !arraysEqual(audienceSegment, appliedAudienceSegment);
  const dateModified = activeStartDate !== activeAppliedStartDate || activeEndDate !== activeAppliedEndDate;
  const segment1Modified = activeSegment1 !== activeAppliedSegment1;
  const segment2Modified = activeSegment2 !== activeAppliedSegment2;
  const segment3Modified = activeSegment3 !== activeAppliedSegment3;
  const advancedFiltersModified =
    JSON.stringify(activeAdvancedFilters) !== JSON.stringify(activeAppliedAdvancedFilters);

  const allObjectTypes = objectTypeOptions.map((o) => o.value);
  const allStatuses = effectiveStatusOptions.map((o) => o.value);
  const isAtDefault =
    !objectTypeModified &&
    !effectiveStatusModified &&
    !(isMyBrand && audienceSegmentModified) &&
    !dateModified &&
    !segment1Modified &&
    !segment2Modified &&
    !segment3Modified &&
    !advancedFiltersModified &&
    arraysEqual(activeObjectType, allObjectTypes) &&
    arraysEqual(activeEffectiveStatus, allStatuses) &&
    (!isMyBrand || audienceSegment.length === 0) &&
    activeSegment1 === DEFAULT_SEGMENT_1 &&
    activeSegment2 === SEGMENT_NONE &&
    activeSegment3 === SEGMENT_NONE &&
    activeAdvancedFilters.length === 0;

  const hasFreeSegmentSlot = activeSegment2 === SEGMENT_NONE || activeSegment3 === SEGMENT_NONE;

  // Dynamic field options derived from the currently selected segments (both modes)
  // plus the currently-selected KPI pills (My Brand only).
  const fieldOptions = useMemo(
    () => buildFilterFieldOptions(mode, activeSegment1, activeSegment2, activeSegment3, myBrandKpis, myBrandById),
    [mode, activeSegment1, activeSegment2, activeSegment3, myBrandKpis, myBrandById],
  );

  // Default field key for new rules: first available segment/KPI, else a mode-correct fallback.
  const defaultFilterField =
    fieldOptions[0]?.key ?? (isMyBrand ? myBrandKpis[0] ?? "spend" : "Days");

  const handleAddFilterRule = () => {
    if (activeAdvancedFilters.length === 0) {
      addFilterGroup();
    } else {
      addFilterRule(activeAdvancedFilters.length - 1, defaultFilterField);
    }
  };

  return (
    <div data-drilldown-filter-bar>
      <div className="flex flex-wrap items-center gap-2 border-t border-border/50 px-5 py-2">
        <MultiSelectPill
          label="Ad Type"
          emptyLabel="All"
          options={objectTypeOptions}
          selected={activeObjectType}
          onChange={setActiveObjectType}
        />
        <MultiSelectPill
          label="Status"
          emptyLabel="All"
          options={effectiveStatusOptions}
          selected={activeEffectiveStatus}
          onChange={setActiveEffectiveStatus}
          shortcuts={isMyBrand ? [{ label: "Inactive Ads", values: [...MY_BRAND_INACTIVE_STATUSES] }] : undefined}
        />
        {isMyBrand ? (
          <MultiSelectPill
            label={t("drilldown.filters.audienceSegment", "Audience segment")}
            emptyLabel="All"
            options={AUDIENCE_SEGMENT_VALUES.map((value) => ({
              value,
              label: audienceSegmentLabel(t, "drilldown", value),
            }))}
            selected={audienceSegment}
            onChange={setAudienceSegment}
          />
        ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                FILTER_TRIGGER_BUTTON,
                "border-dashed text-muted-foreground hover:text-foreground",
              )}
            >
              <Plus className="h-3 w-3 shrink-0" aria-hidden /> Filter
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            <DropdownMenuItem onSelect={handleAddFilterRule}>
              Add filter rule
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => addFilterGroup()}>
              Add filter group
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">
          <SegmentPill
            value={activeSegment1}
            onChange={(v) => setSegment(1, v)}
            options={segmentOptions}
          />
          {activeSegment2 !== SEGMENT_NONE && (
            <SegmentPill
              value={activeSegment2}
              onChange={(v) => setSegment(2, v)}
              onRemove={() => removeSegment(2)}
              options={segmentOptions}
            />
          )}
          {activeSegment3 !== SEGMENT_NONE && (
            <SegmentPill
              value={activeSegment3}
              onChange={(v) => setSegment(3, v)}
              onRemove={() => removeSegment(3)}
              options={segmentOptions}
            />
          )}
          {hasFreeSegmentSlot && (
            <button
              type="button"
              onClick={() => addSegment("Ad ID")}
              className={cn(
                FILTER_TRIGGER_BUTTON,
                "border-dashed text-muted-foreground hover:text-foreground",
              )}
            >
              <Plus className="h-3 w-3 shrink-0" aria-hidden /> Group By
            </button>
          )}
          {!isAtDefault && (
            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={reset}>
              <RotateCcw className="mr-1 h-3 w-3" />
              Reset
            </Button>
          )}
          <DrilldownFilterPresetSelector
            mode={mode}
            filtersHydrated={filtersHydrated}
            onPresetApplied={onPresetApplied}
            applySignal={applySignal}
            onPendingPresetChange={onPendingPresetChange}
            selectPresetId={selectPresetId}
          />
          <DrilldownSaveFilterPreset
            mode={mode}
            canSavePreset={canSavePreset}
            onPresetCreated={setSelectPresetId}
          />
          {trailingActions}
        </div>
      </div>

      {activeAdvancedFilters.length > 0 && (
        <AdvancedFiltersEditor
          className="border-t border-border/50 px-5 py-3"
          groups={activeAdvancedFilters}
          fieldOptions={fieldOptions}
          onSetConnector={setGroupConnector}
          onAddRule={(gIdx) => addFilterRule(gIdx, defaultFilterField)}
          onRemoveGroup={removeFilterGroup}
          onUpdateRule={updateFilterRule}
          onRemoveRule={removeFilterRule}
        />
      )}
    </div>
  );
}
